/**
 * Initialize and send analytics data.
 *
 * @module core/analytics
 */

import * as App from './app';
import * as Filter from './filter';
import * as View from './view';
import * as Util from './util';

/**
 * Default analytics configuration settings. These settings are exported to app default [`settings`]{@link module:core/app.settings}. For more information on configuration options, see [analytics content settings]{@link module:core/config.analytics}.
 *
 * @type {object}
 *
 * @since 3.4.2
 */
export const config = {
  enabled: false,
  in_app: false,
  debug: false,
  id: '',
  parent_origin: false,
  schema: {
    global: {
      type: 'string',
    },
    discrete_graphics_model: {
      rename: 'graphics_model',
    },
    game_fps: {
      rename: 'game',
    },
    'game_fps:quality': {
      rename: 'game_quality',
    },
    'game_fps:quality:fps': {
      rename: 'game_quality_fps',
    },
    path: {
      rename: 'path_location',
    },
    resolution_name: {
      rename: 'resolution',
    },
  },
  prevEvent: {},
};

/**
 * Initialize analytics scripts and send GTM configuration data.
 *
 * - Under certain conditions, analytics will not be initialized. In this case, a message will be posted to the parent page (if iframe) listing any reasons why. These conditions are:
 *     - [analytics setting]{@link module:core/config.analytics} is not enabled
 *     - no analytics `id` has been provided
 *     - `dev_mode` [app setting]{@link module:core/config.app_settings} is enabled.
 * - [Initialize iframe `postMessage()` listener]{@link module:core/analytics~postMessageInit}, if necessary.
 * - [Initiate Google Analytics script]{@link module:core/analytics~gaScriptInit}, if necessary.
 *
 * @since 3.4.2
 *
 * @method
 *
 * @see [`App.updateSettings`]{@link module:core/app.updateSettings}
 * @see [`App.getAttributeDef`]{@link module:core/app.getAttributeDef}
 * @see [`postMessageInit`]{@link module:core/analytics~postMessageInit}
 * @see [`gaScriptInit`]{@link module:core/analytics~gaScriptInit}
 * @see Used by:
 * @see [**`App.init`**]{@link module:core/app.init}
 */
export const init = () => {
  // if core analytics is not enabled, no analytics ID is provided, or in analytics dev mode, don't initialize analytics
  // label reasons so they can be reported
  let initFailConditions = {
    'analytics not enabled': !App.settings.analytics.enabled,
    'no analytics id provided': !App.settings.analytics.id,
    'dev mode': App.settings.dev_mode,
  };

  // get array of fail condition messages
  let cond = Object.entries(initFailConditions).filter((entry) => entry[1]).map(([reason]) => reason);

  // stop if any fail conditions exist
  if (cond.length) {
    let message = `Analytics init prevented [${cond.join(' | ')}]`;
    debug(message);
    // post failure message, if not in-app
    if (!App.settings.analytics.in_app) {
      window.parent.postMessage({
        event: 'no_analytics',
        message,
      }, '*');
    }
    // don't go any further
    return;
  }

  // set analytics attribute def, and disable analytics if no ID is present
  App.updateSettings({
    analytics: {
      enabled: App.settings.analytics.id && App.settings.analytics.enabled,
      def: App.getAttributeDef('analytics'),
    },
  });

  let initData = {
    event: 'gtm.js',
    'gtm.start': new Date().getTime(),
  };

  // setup event listeners
  postMessageInit(initData);

  // initialize GA scripts
  gaScriptInit(initData);
};

/**
 * Create and send GA4 analytics event object. All event objects contain the same [default event data]{@link module:core/analytics~eventDefaults}. Additional optional `data` may be passed in to the event object, which will be assigned to the event object and passed directly with the event data.
 *
 * After the first event has fired, subsequent events will first clear any optional data that was pushed by the previous event, to prevent irrelevant data from persisting in the GTM model. This is done by setting the values to `'(not set)'` and pushing the 'reset object' to the datalayer with no event param.
 *
 * Returns event object data after [pushing the event]{@link module:core/analytics~eventPush}.
 *
 * *Note that the `referrer` property in the event object will vary depending on deployment method.*
 *
 * - *If a retailer is using a remotely-hosted build in an iframe, the `referrer` value will be the page the iframe is embedded in, and the `href` will be the URL of the remotely-hosted app (e.g. in AWS S3). In this case, the relevant location value is the `referrer` value.*
 *
 * - *If a retailer is using a non-iframe build, or an iframe with contents that are loaded directly in the iframe (non-remote content), the `href` will be the page the app is located on, and the `referrer` is the page that directed the user to the current page (i.e. the traditional "referrer" meaning). In this case, the relevant location value is the `href` value.*
 *
 * @since 3.4.6
 *
 * @method
 *
 * @param {string} event - Event name, which is sent directly to GA4 as the `event` property value.
 * @param {object} [data] - Additional data to include in the analytics event object. Default: `{}`
 *
 * @return {object}
 *
 * @see [`resetParams`]{@link module:core/analytics~resetParams}
 * @see [`eventDefaults`]{@link module:core/analytics~eventDefaults}
 * @see [`eventPush`]{@link module:core/analytics~eventPush}
 * @see Used by:
 * @see [**`inputEvent`**]{@link module:core/analytics.inputEvent}
 * @see [**`ecommerceEvent`**]{@link module:core/analytics.ecommerceEvent}
 * @see [**`recommendEvent`**]{@link module:core/analytics.recommendEvent}
 * @see [**`Form.select.expand`**]{@link module:core/form.select.expand}
 * @see [**`Offline.resetInactivityTimer`**]{@link module:core/offline~resetInactivityTimer}
 * @see [**`View.navigate`**]{@link module:core/view.navigate}
 * @see [**`View.navigateOffsite`**]{@link module:core/view.navigateOffsite}
 * @see [**`View.addEventListeners`**]{@link module:core/view~addEventListeners}
 * @see [**`View.facet.addEventListeners`**]{@link module:core/view.facet.addEventListeners}
 *
 * @example
 * // custom event data
 * let data = {
 *   option_name: 'Everyday tasks',
 *   option_value: 'Multitasking',
 *   processor: ['i5'],
 *   usage_models: ['multitasking'],
 * };
 *
 * // trigger event
 * event('selected_option', data);
 *
 * // pushed event object
 * {
 *   event: 'selected_option',
 *   allow_google_signals: false,
 *   analytics_lang: 'en',
 *   app_id: 'srt-global',
 *   app_vendor: 'staples',
 *   app_version: '4.2.6',
 *   environment: 'production',
 *   option_name: 'Everyday tasks',
 *   option_value: 'Multitasking',
 *   page_id: 'category_everyday_tasks',
 *   page_title: 'I want to use my computer for:',
 *   path_location: ['overwhelmed'],
 *   processor: ['i5'],
 *   referrer: '',
 *   step: 2,
 *   usage_models: ['multitasking'],
 * }
 */
export const event = (event, data = {}) => {
  // reset GTM model from previous events
  resetParams(data);
  // initialize event object with default values and custom data
  let eventObj = Object.assign({event}, eventDefaults(), data);
  // show analytics event
  debug(`%c${event}`, 'font-weight:bold; color:lightgreen;', eventObj);
  // push and return object
  eventPush(eventObj);
  return eventObj;
};

/**
 * Handle user selection input events. Returns the event object.
 *
 * - If `deselect` or `passive` params are enabled, do nothing.
 * - Get analytics [attribute definition]{@link module:core/app.getAttributeDef} and [attributes]{@link module:core/view.inputAttributes} for the given `input`.
 * - Get current `option_name` (input group label) and `option_value` (input label). The most relevant label for the current input is determined by walking the full content path to the input data and storing the nearest `title` or `label` value.
 * - Run `selected_option` [event]{@link module:core/analytics.event} and relevant data.
 *
 * @since 3.4.6
 *
 * @method
 *
 * @param {array|jQuery} input - Input for which event is running. See [`View.inputAttributes`]{@link module:core/view.inputAttributes} `input` param for more info on valid input values.
 *
 * @param {boolean} deselect - Whether the input is currently being deselected. Default: `false`
 *
 * @param {boolean} passive - Whether the input is being interacted with passively. Default: `false`
 *
 * @return {object}
 *
 * @see [`App.getAttributeDef`]{@link module:core/app.getAttributeDef}
 * @see [`View.inputAttributes`]{@link module:core/view.inputAttributes}
 * @see [`View.getElementID`]{@link module:core/view.getElementID}
 * @see [`event`]{@link module:core/analytics.event}
 * @see Used by:
 * @see [**`Form.button.setState`**]{@link module:core/form.button.setState}
 * @see [**`Form.radio.setState`**]{@link module:core/form.radio.setState}
 * @see [**`Form.checkbox.setState`**]{@link module:core/form.checkbox.setState}
 * @see [**`Form.select.setState`**]{@link module:core/form.select.setState}
 */
export const inputEvent = (input, deselect = false, passive = false) => {
  // do nothing if passive or deselecting
  if (deselect || passive) return;
  // get input attributes
  let attrDef = App.getAttributeDef('analytics');
  let eventData = View.inputAttributes(input, attrDef, true);
  // get current option label
  let inputID = Util.decodeID(View.getElementID(input));
  Util.walkObjectPath(App.current, inputID, (o, r) => {
    if (o.title || o.label) r.option_name = o.title || o.label;
    return r;
  }, eventData);
  // get selection option text
  eventData.option_value = (input.text() || input.next('label').text()).trim();
  // trigger event
  return event('selected_option', eventData);
};

/**
 * Prepare product data for submission as an enhanced ecommerce analytics event.
 * Returns the event object.
 *
 * [Products are retrieved]{@link module:core/filter.getProductsByID} and formatted into the appropriate [event structure]{@link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm} (`view_item_list` or `select_item`) before being sent to the main [`event`]{@link module:core/analytics.event} method.
 *
 * @since 3.4.6
 *
 * @method
 *
 * @param {string} name - Event name.
 *
 * @param {...string} productIDs - Product IDs for which to retrieve product objects.
 *
 * @return {object}
 *
 * @see [`Filter.getProductsByID`]{@link module:core/filter.getProductsByID}
 * @see [`event`]{@link module:core/analytics.event}
 * @see Used by:
 * @see [**`recommendEvent`**]{@link module:core/analytics.recommendEvent}
 * @see [**`View.navigateOffsite`**]{@link module:core/view.navigateOffsite}
 */
export const ecommerceEvent = (name, ...productIDs) => {
  // get product data
  let products = Filter.getProductsByID(...productIDs);
  // function to format categories
  let getSpec = (product, i) => product.attributes[App.settings.filter.display_product_specs[i]];
  // create event object data
  let data = {
    ecommerce: {
      items: products.map((product) => {
        return {
          item_name: product.name,
          item_id: product.product_id,
          price: Util.round(2 - Util.countDecimals(product.attributes.price) ? product.attributes.price - 0.01 : product.attributes.price, 2),
          item_brand: product.brand,
          index: App.state.current.products.indexOf(product.product_id),
          item_category: getSpec(product, 0),
          item_category2: getSpec(product, 1),
          item_category3: getSpec(product, 2),
          item_category4: getSpec(product, 3),
          item_category5: getSpec(product, 4),
        }
      }),
    },
  };
  // send event
  return event(name, data);
};

/**
 * Runs all recommendation-related events:
 *
 * - `view_recommendations` - [`event`]{@link module:core/analytics.event}
 * - `selected_attributes` - [`event`]{@link module:core/analytics.event} with current analytics attributes object
 * - `view_item_list` - [`ecommerceEvent`]{@link module:core/analytics.ecommerceEvent} with all current product IDs.
 *
 * Returns an array containing event objects from each event.
 *
 * @since 3.4.6
 *
 * @method
 *
 * @return {array}
 *
 * @see [`event`]{@link module:core/analytics.event}
 * @see [`ecommerceEvent`]{@link module:core/analytics.ecommerceEvent}
 * @see Used by:
 * @see [**`View.navigate`**]{@link module:core/view.navigate}
 * @see [**`View.navigateOffsite`**]{@link module:core/view.navigateOffsite}
 */
export const recommendEvent = () => {
  let view_recommendations = event('view_recommendations');
  let selected_attributes = event('selected_attributes', App.state.attr.analytics);
  let view_item_list = ecommerceEvent('view_item_list', ...(App.state.current.products || []));
  return [view_recommendations, selected_attributes, view_item_list];
};

/**
 * Add event listener for a `postMessage()` event from a valid syndication origin source, if `in_app` [analytics setting]{@link module:core/config.analytics} is not enabled.
 *
 * - If the message origin is unknown, and a message value of `srt-init` is received, a return message indicating an unknown origin is sent.
 * - If a message is received with a value of `srt-init`, the parent origin is set in analytics config object.
 * - If a message is received with an object containing a `session_id` value, `App.settings.analytics` are updated with this value.
 *
 * @since 3.4.6
 *
 * @param {object} initData - Analytics initialization data to be sent with event.
 *
 * @see [`event`]{@link module:core/analytics.event}
 * @see [`App.updateSettings`]{@link module:core/app.updateSettings}
 * @see Used by:
 * @see [**`init`**]{@link module:core/analytics.init}
 */
const postMessageInit = (initData) => {
  // if running in-app analytics, do nothing
  if (App.settings.analytics.in_app) return;
  // create event listener for parent page
  // set valid sources
  let src = Object.values(App.settings.syndication || {});
  // stop here if no valid sources provided
  if (!src.length) return;
  // map URL basenames
  src = src.map(a => Util.urlBasename(a));
  // create listener for parent messages
  window.addEventListener("message", (e) => {
    // stop here if config src is not accepted
    if (!src.includes(e.origin)) {
      // if init message is received from an unrecognized origin, report it
      if (e.data === 'srt-init') {
        window.parent.postMessage({
          event: 'no_analytics',
          message: 'Analytics init prevented [unknown origin]',
        }, '*');
      }
      return;
    }
    // check for session_id value
    if (typeof e.data === 'object' && e.data.session_id) {
      let session_id = e.data.session_id;
      App.updateSettings({analytics: {session_id}})
    }
    // stop here init signal is not sent, or parent_origin has not yet been set
    if (e.data !== 'srt-init' || config.parent_origin) return;
    // set parent origin value
    config.parent_origin = e.origin;
    // send ready response
    eventPush(initData);
  });
};

/**
 * Initialize Google Analytics script, if `in_app` [analytics setting]{@link module:core/config.analytics} is enabled.
 *
 * @since 3.4.6
 *
 * @param {object} initData - Analytics initialization data to be sent with event.
 *
 * @see [`event`]{@link module:core/analytics.event}
 * @see [`getGASessionID`]{@link module:core/analytics~getGASessionID}
 * @see Used by:
 * @see [**`init`**]{@link module:core/analytics.init}
 */
const gaScriptInit = (initData) => {
  // if not running in-app analytics, do nothing
  if (!App.settings.analytics.in_app) return;
  // set up analytics script
  window.srtDataLayer = window.srtDataLayer || [];
  eventPush(initData);

  // generate script
  let script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${App.settings.analytics.id}&l=srtDataLayer`;
  document.getElementsByTagName('body')[0].appendChild(script);

  // get session ID
  getGASessionID();
};

/**
 * Access GA4 session ID. Gets value of GA cookie and uses regex to isolate the session ID, which is stored as `session_id` in `App.settings.analytics` settings.
 *
 * @see [`App.updateSettings`]{@link module:core/app.updateSettings}
 * @see Used by:
 * @see [**`gaScriptInit`**]{@link module:core/analytics~gaScriptInit}
 */
const getGASessionID = () => {
  const cookieID = '_ga_1FXH2MVPJE';
  const pattern = new RegExp(`${cookieID}=GS\\d\\.\\d\\.(.+?)(?:;|$)`);
  const match = document.cookie.match(pattern);
  const parts = match?.[1].split(".");

  if (!parts) {
    // Cookie not yet available; wait a bit and try again.
    window.setTimeout(() => getGASessionID(), 500);
    return;
  }

  let session_id = parts.shift();

  App.updateSettings({
    analytics: { session_id },
  });
};

/**
 * Resets any custom data params from previous events by setting them to `"(not set)"` and pushing the event to the datalayer.
 *
 * *Note that all custom data from the previous event is cleared, even if the new event's custom data contains overlapping params.*
 *
 * @since 3.5.0
 *
 * @param {object} data - Custom data to store as the new previous event.
 *
 * @see [`eventPush`]{@link module:core/analytics~eventPush}
 * @see Used by:
 * @see [**`event`**]{@link module:core/analytics.event}
 *
 * @example
 * // previous event's custom data, stored in config
 * let data = {
 *   option_name: 'Everyday tasks',
 *   option_value: 'Multitasking',
 *   processor: ['i5'],
 *   usage_models: ['multitasking'],
 * };
 *
 * // new event data to store in config
 * let data = {
 *   navigation_direction: 'forward',
 * }
 *
 * // run reset
 * resetParams(data);
 *
 * // reset object pushed to datalayer
 * {
 *   option_name: '(not set)',
 *   option_value: '(not set)',
 *   processor: '(not set)',
 *   usage_models: '(not set)',
 * }
 */
const resetParams = (data) => {
  // get keys from previous event object
  let prev = Object.keys(config.prevEvent);
  // copy current data params to event new object
  config.prevEvent = Object.assign({}, data);
  // if no previous event keys, no params need clearing
  if (!prev.length) return;
  // create reset object to hold params that need clearing
  let resetObj = {};
  // clear previous params, unless contained in current data
  for (let param of prev) if (typeof data[param] === 'undefined') resetObj[param] = '(not set)';
  // if no new reset param keys, no params need clearing
  if (!Object.keys(resetObj).length) return;
  debug(`%cclearing parameters:`, 'color:grey;', Object.keys(resetObj).join(', '));
  // push reset object
  eventPush(resetObj);
};

/**
 * Returns default event parameters. These include:
 *
 * - `app_id` - app_id setting value
 * - `app_vendor` - app_vendor setting value
 * - `app_version` - app_version setting value
 * - `analytics_lang` - lang setting value
 * - `referrer` - referrer URL
 * - `environment` - current build environment
 * - `allow_google_signals` - possibly included for legacy reasons?
 * - `path_location` - the current user path
 * - `step` - the current user path depth
 * - `page_id` - current page ID
 * - `page_title` - current page title
 *
 * @since 3.5.0
 *
 * @returns {object}
 *
 * @see Used by:
 * @see [**`event`**]{@link module:core/analytics.event}
 *
 * @example
 * {
 *   app_id: 'srt-global',
 *   app_vendor: 'intel',
 *   app_version: '1.1.0',
 *   analytics_lang: 'en',
 *   referrer: 'https://retailer.com/srt',
 *   environment: 'production',
 *   allow_google_signals: false,
 *   path_location: ['knowledgeable],
 *   step: 3,
 *   page_id: 'device_brand',
 *   page_title: 'Which brand do you prefer?',
 * }
 */
const eventDefaults = () => {
  return {
    app_id: App.settings.app_id,
    app_vendor: App.settings.app_vendor,
    app_version: App.settings.app_version,
    analytics_lang: App.settings.lang,
    page_id: (App.state.current && App.state.current.location) || App.settings.start,
    page_title: App.current.title,
    environment: App.settings.env,
    referrer: document.referrer,
    allow_google_signals: false,
    step: App.state.path_depth || 0,
    path_location: (App.state.attr && App.state.attr.analytics.path_location) || 'none',
  };
};

/**
 * Push stored event.
 *
 * If `in_app` [analytics setting]{@link module:core/config.analytics} is enabled, the event will be pushed to the current window's `srtDataLayer`. Otherwise, event will be posted to the parent window using the [`Window.postMessage()`]{@link https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage} method.
 *
 * Event is not pushed if `dev_mode` is enabled or `env` is "matrix" (see [app settings]{@link module:core/config.app_settings}).
 *
 * @since 3.4.2
 *
 * @param {object} eventObj - Analytics event object to push.
 *
 * @see Used by:
 * @see [**`event`**]{@link module:core/analytics.event}
 */
const eventPush = (eventObj) => {
  // if analytics push should not occur, stop here
  if (!App.settings.analytics.enabled || App.settings.dev_mode || App.settings.env === 'matrix') return;
  // push stored event to datalayer, if running in-app
  if (App.settings.analytics.in_app) srtDataLayer.push(eventObj);
  // otherwise post event to parent window if not running
  else {
    // do nothing if parent origin is not available
    if (!config.parent_origin) return;
    window.parent.postMessage(eventObj, config.parent_origin);
  }
};

const debug = (...content) => {
  if (App.settings.analytics.debug) {
    console.log(...content);
  }
};
